import * as Api from '@ViewModels';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	AutomationEditorStep,
	AutomationStepWithHelpers,
	getStepType,
	isSwitchStep,
} from '../../../../models/Automations';
import { useAutomationTemplateRemoveStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateRemoveStepMutation';
import { useAutomationTemplateQuery } from '../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { validateAutomationStep } from '../../../utils/automation-validation';

/**
 * Fetches a child automation template by ID.
 */
async function fetchChildTemplate(
	userSession: ReturnType<typeof useUserSession>,
	childTemplateId: string,
	impersonationContext?: Api.IImpersonationContext
): Promise<Api.IAutomationTemplate | null> {
	try {
		return await userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `AutomationTemplate/${encodeURIComponent(childTemplateId)}`,
				impersonationContext,
			}),
			'GET'
		);
	} catch (error) {
		console.error(`Failed to fetch child template ${childTemplateId}:`, error);
		return null;
	}
}

/**
 * Custom hook for managing automation editor steps.
 * Replaces the EditAutomationTemplateStepsViewModel with React Query + React hooks.
 *
 * @param templateId - The ID of the automation template
 * @param impersonationContext - Optional impersonation context for API calls
 * @returns Object containing steps state and management functions
 */
export function useAutomationEditorSteps({
	templateId,
	impersonationContext,
}: {
	templateId: string | undefined;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const [steps, setSteps] = useState<AutomationEditorStep[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const templateQuery = useAutomationTemplateQuery({
		id: templateId || '',
		enabled: !!templateId,
		impersonationContext,
	});

	const removeStepMutation = useAutomationTemplateRemoveStepMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const createUuid = useCallback(() => {
		return typeof crypto !== 'undefined' && crypto.randomUUID
			? crypto.randomUUID()
			: Math.random().toString(36).slice(2);
	}, []);

	const sanitizeAutomationStep = useCallback((step?: Api.IAutomationStep): Api.IAutomationStep => {
		if (!step) {
			return {} as Api.IAutomationStep;
		}

		// Strip helper props from any previously enhanced step
		const {
			model: _model,
			setModel: _setModel,
			update: _update,
			toJs: _toJs,
			addConditionalStep: _addConditionalStep,
			removeConditionalStep: _removeConditionalStep,
			hasAllRequiredInfo: _hasAllRequiredInfo,
			showRequiredFieldError,
			canEdit,
			isBusy,
			templateId: _stepTemplateId,
			impersonationContext: _impersonationContext,
			...rest
		} = step as Api.IAutomationStep & AutomationStepWithHelpers;

		const conditionalSteps = rest.conditionalSteps
			? Object.entries(rest.conditionalSteps).reduce<Record<string, Api.IAutomationStep>>((acc, [key, value]) => {
					acc[key] = sanitizeAutomationStep(value as Api.IAutomationStep);
					return acc;
				}, {})
			: undefined;

		return {
			...rest,
			...(conditionalSteps ? { conditionalSteps } : {}),
			...(showRequiredFieldError !== undefined ? { showRequiredFieldError } : {}),
		};
	}, []);

	const buildAutomationStep = useCallback(
		(
			model: Api.IAutomationStep | undefined,
			stepMeta: {
				templateId?: string;
				impersonationContext?: Api.IImpersonationContext;
				canEdit?: boolean;
			},
			stepId: string
		): AutomationStepWithHelpers => {
			const baseModel = sanitizeAutomationStep(model);
			const stepUpdater = (updater: (current: Api.IAutomationStep) => Api.IAutomationStep) => {
				setSteps(prev =>
					prev.map(step => {
						if (step.uuid !== stepId) {
							return step;
						}
						const currentModel =
							(step.automationStep?.model as Api.IAutomationStep) ||
							(step.automationStep as Api.IAutomationStep) ||
							({} as Api.IAutomationStep);
						const nextModel = sanitizeAutomationStep(updater(currentModel));
						return {
							...step,
							automationStep: buildAutomationStep(nextModel, step, stepId),
						};
					})
				);
			};

			const enhanceConditionalStep = (
				conditionalStep: Api.IAutomationStep | undefined,
				outcome?: Api.AutomationStepOutcome
			): AutomationStepWithHelpers | undefined => {
				if (!conditionalStep) {
					return undefined;
				}
				const conditionalModel = sanitizeAutomationStep(conditionalStep);
				return {
					...conditionalModel,
					type: getStepType(conditionalModel),
					model: conditionalModel,
					hasAllRequiredInfo: validateAutomationStep(conditionalModel),
					showRequiredFieldError: (conditionalStep as AutomationStepWithHelpers)?.showRequiredFieldError ?? false,
					setModel: updates =>
						stepUpdater((current: Api.IAutomationStep) => {
							const conditional = { ...(current.conditionalSteps || {}) };
							conditional[outcome] = sanitizeAutomationStep({
								...(conditional[outcome] || {}),
								...updates,
							});
							return {
								...current,
								conditionalSteps: conditional,
							};
						}),
					update: updates =>
						stepUpdater((current: Api.IAutomationStep) => {
							const conditional = { ...(current.conditionalSteps || {}) };
							conditional[outcome] = sanitizeAutomationStep({
								...(conditional[outcome] || {}),
								...updates,
							});
							return {
								...current,
								conditionalSteps: conditional,
							};
						}),
					toJs: () => sanitizeAutomationStep(conditionalModel),
				};
			};

			const conditionalSteps = baseModel?.conditionalSteps
				? Object.entries(baseModel.conditionalSteps).reduce<Record<string, AutomationStepWithHelpers>>(
						(acc, [key, value]) => {
							acc[key] = enhanceConditionalStep(value as Api.IAutomationStep, key as Api.AutomationStepOutcome);
							return acc;
						},
						{}
					)
				: undefined;

			const automationStep: AutomationStepWithHelpers = {
				...baseModel,
				...(conditionalSteps ? { conditionalSteps } : {}),
				type: getStepType(baseModel),
				model: baseModel,
				templateId: stepMeta.templateId,
				impersonationContext: stepMeta.impersonationContext,
				canEdit: stepMeta.canEdit ?? true,
				isBusy: false,
				showRequiredFieldError: (baseModel as AutomationStepWithHelpers)?.showRequiredFieldError ?? false,
				hasAllRequiredInfo: validateAutomationStep(baseModel),
				setModel: updates =>
					stepUpdater(current => {
						return { ...current, ...updates };
					}),
				update: updates =>
					stepUpdater(current => {
						return { ...current, ...updates };
					}),
				toJs: () => sanitizeAutomationStep(baseModel),
				addConditionalStep: (conditionalStep, outcome) =>
					stepUpdater(current => {
						const conditional = { ...(current.conditionalSteps || {}) };
						conditional[outcome] = sanitizeAutomationStep(conditionalStep);
						return { ...current, conditionalSteps: conditional };
					}),
				removeConditionalStep: outcome =>
					stepUpdater(current => {
						const conditional = { ...(current.conditionalSteps || {}) };
						delete conditional[outcome];
						return { ...current, conditionalSteps: conditional };
					}),
			};

			return automationStep;
		},
		[sanitizeAutomationStep]
	);

	/**
	 * Builds steps array from template data without triggering a refetch.
	 * Used internally to sync local state with React Query cache.
	 */
	const buildStepsFromTemplate = useCallback(
		(templateData: Api.IAutomationTemplate): AutomationEditorStep[] => {
			const version = templateData.draft || templateData.published;
			const meta = {
				templateId: templateData.id,
				impersonationContext,
				canEdit: templateData.canEdit ?? true,
			};

			const builtSteps: AutomationEditorStep[] =
				version?.steps.map((stepData: Api.IAutomationStep) => {
					const uuid = stepData?.id || createUuid();
					return {
						uuid,
						templateId: meta.templateId,
						impersonationContext: meta.impersonationContext,
						canEdit: meta.canEdit,
						automationStep: buildAutomationStep(stepData, meta, uuid),
					};
				}) || [];

			if (builtSteps.length === 0) {
				const uuid = createUuid();
				builtSteps.push({
					uuid,
					templateId: meta.templateId,
					impersonationContext: meta.impersonationContext,
					canEdit: meta.canEdit,
					automationStep: buildAutomationStep(undefined, meta, uuid),
				});
			}

			return builtSteps;
		},
		[impersonationContext, createUuid, buildAutomationStep]
	);

	// Track the last processed dataUpdatedAt to detect actual data changes
	const lastDataUpdatedAtRef = useRef<number | null>(null);

	/**
	 * Sync local steps state with React Query data when it changes.
	 * This ensures UI updates automatically when mutations invalidate the cache.
	 */
	useEffect(() => {
		// Only process if we have data and it's actually changed
		if (!templateQuery.data || !templateQuery.dataUpdatedAt) {
			return;
		}

		// Skip if we've already processed this data version
		if (lastDataUpdatedAtRef.current === templateQuery.dataUpdatedAt) {
			return;
		}

		lastDataUpdatedAtRef.current = templateQuery.dataUpdatedAt;
		const newSteps = buildStepsFromTemplate(templateQuery.data);
		setSteps(newSteps);
	}, [templateQuery.data, templateQuery.dataUpdatedAt, buildStepsFromTemplate]);

	const updateStep = useCallback(
		(stepId: string, updater: (step: Api.IAutomationStep) => Api.IAutomationStep) => {
			setSteps(prev =>
				prev.map(step => {
					if (step.uuid !== stepId) {
						return step;
					}
					const currentModel =
						(step.automationStep?.model as Api.IAutomationStep) ||
						(step.automationStep as Api.IAutomationStep) ||
						({} as Api.IAutomationStep);
					const nextModel = sanitizeAutomationStep(updater(currentModel));
					return {
						...step,
						automationStep: buildAutomationStep(nextModel, step, stepId),
					};
				})
			);
		},
		[buildAutomationStep, sanitizeAutomationStep]
	);

	const setShowRequiredFieldError = useCallback(
		(stepId: string, value: boolean) => {
			updateStep(stepId, current => ({
				...current,
				showRequiredFieldError: value,
			}));
		},
		[updateStep]
	);

	/**
	 * Checks if all steps have IDs, which indicates they can be reordered.
	 */
	const canReorderSteps = steps.every(step => !!step.automationStep?.id);

	/**
	 * Loads steps from the automation template.
	 *
	 * @param automationTemplate - The automation template containing step data
	 * @param reloadTemplate - If true, refetch the template data before loading steps
	 */
	const loadSteps = useCallback(
		async (automationTemplate: Api.IAutomationTemplate, reloadTemplate = true) => {
			if (!automationTemplate) {
				return;
			}

			setIsLoading(true);

			try {
				let templateData = automationTemplate;

				if (reloadTemplate) {
					const result = await templateQuery.refetch();
					// Use the refetched data if available
					if (result.data) {
						templateData = result.data;
					}
				}

				const version = templateData.draft || templateData.published;

				const meta = {
					templateId: templateData.id,
					impersonationContext,
					canEdit: templateData.canEdit ?? true,
				};
				const loadedSteps: AutomationEditorStep[] =
					version?.steps.map((stepData: Api.IAutomationStep) => {
						const uuid = stepData?.id || createUuid();
						return {
							uuid,
							templateId: meta.templateId,
							impersonationContext: meta.impersonationContext,
							canEdit: meta.canEdit,
							automationStep: buildAutomationStep(stepData, meta, uuid),
						};
					}) || [];

				if (loadedSteps.length === 0) {
					const uuid = createUuid();
					loadedSteps.push({
						uuid,
						templateId: meta.templateId,
						impersonationContext: meta.impersonationContext,
						canEdit: meta.canEdit,
						automationStep: buildAutomationStep(undefined, meta, uuid),
					});
				}

				setSteps(loadedSteps);
			} finally {
				setIsLoading(false);
			}
		},
		[templateQuery, buildAutomationStep, createUuid, impersonationContext]
	);

	/**
	 * Adds a new step to the steps array.
	 *
	 * @param step - Optional step to add. If not provided, creates an empty step.
	 * @returns Promise that resolves to the updated steps array
	 */
	const addStep = useCallback(
		async (step?: AutomationEditorStep) => {
			const meta = {
				templateId,
				impersonationContext,
				canEdit: true,
			};
			const nextStep =
				step ||
				(() => {
					const uuid = createUuid();
					return {
						uuid,
						templateId: meta.templateId,
						impersonationContext: meta.impersonationContext,
						canEdit: meta.canEdit,
						automationStep: buildAutomationStep(undefined, meta, uuid),
					};
				})();
			const updatedSteps = [...steps, nextStep as AutomationEditorStep];
			setSteps(updatedSteps);
			return updatedSteps;
		},
		[steps, templateId, impersonationContext, buildAutomationStep, createUuid]
	);

	/**
	 * Removes a step from the steps array.
	 * If the step has an ID, it will also be removed from the template via API.
	 *
	 * @param step - The step to remove
	 * @returns Promise that resolves to the updated steps array
	 */
	const removeStep = useCallback(
		async (step: AutomationEditorStep) => {
			const index = steps.findIndex(x => x.uuid === step.uuid);
			if (index < 0) {
				return steps;
			}

			if (step.automationStep?.id && templateId) {
				const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(step.automationStep);
				if (stepType) {
					await removeStepMutation.mutateAsync({
						templateId,
						stepType,
						stepId: step.automationStep.id,
						impersonationContext,
					});
				}
			}

			const updatedSteps = [...steps];
			updatedSteps.splice(index, 1);
			setSteps(updatedSteps);
			return updatedSteps;
		},
		[steps, templateId, impersonationContext, removeStepMutation]
	);

	/**
	 * Gets all incomplete steps that have missing required information.
	 * Checks both main steps and nested steps within switch statements recursively.
	 * Returns a promise that resolves to the array of incomplete steps.
	 *
	 * @returns Promise that resolves to array of steps with incomplete required information
	 */
	const getIncompleteSteps = useCallback(async (): Promise<AutomationEditorStep[]> => {
		const incompleteSteps: AutomationEditorStep[] = [];
		const validSteps = steps.filter(step => !!step.automationStep?.id);
		const visitedTemplateIds = new Set<string>();

		const checkStepRecursively = async (step: AutomationEditorStep): Promise<void> => {
			if (!step.automationStep) {
				return;
			}

			if (!isSwitchStep(step.automationStep)) {
				if (!validateAutomationStep(step.automationStep)) {
					incompleteSteps.push(step);
				}
				return;
			}

			const switchStep = step.automationStep;
			if (!validateAutomationStep(switchStep)) {
				incompleteSteps.push(step);
			}

			for (const caseStatement of switchStep.cases || []) {
				const caseTemplateId = caseStatement.automationTemplateId;
				if (!caseTemplateId || visitedTemplateIds.has(caseTemplateId)) {
					continue;
				}

				visitedTemplateIds.add(caseTemplateId);
				const childTemplate = await fetchChildTemplate(userSession, caseTemplateId, impersonationContext);
				const version = childTemplate?.draft || childTemplate?.published;
				if (!version?.steps) {
					continue;
				}

				for (const childStepData of version.steps) {
					const childStep: AutomationEditorStep = {
						uuid: childStepData.id || createUuid(),
						templateId: childTemplate.id,
						impersonationContext,
						canEdit: childTemplate.canEdit ?? true,
						automationStep: buildAutomationStep(
							childStepData,
							{ templateId: childTemplate.id, impersonationContext, canEdit: childTemplate.canEdit ?? true },
							childStepData.id || createUuid()
						),
					};
					await checkStepRecursively(childStep);
				}
			}
		};

		if (templateId) {
			visitedTemplateIds.add(templateId);
		}
		for (const step of validSteps) {
			await checkStepRecursively(step);
		}
		return incompleteSteps;
	}, [steps, templateId, impersonationContext, userSession, createUuid, buildAutomationStep]);

	return {
		steps,
		isLoading: isLoading || templateQuery.isLoading,
		canReorderSteps,
		addStep,
		removeStep,
		loadSteps,
		getIncompleteSteps,
		updateStep,
		setShowRequiredFieldError,
		setStepsState: setSteps,
		// Expose query data and state for direct use
		templateData: templateQuery.data,
		templateDataUpdatedAt: templateQuery.dataUpdatedAt,
		isTemplateLoading: templateQuery.isLoading,
		isTemplateError: templateQuery.isError,
		templateError: templateQuery.error,
		refetchTemplate: templateQuery.refetch,
	};
}
